import React from "react";
import { useSelector } from "react-redux";
import { constants } from "@qgiv/core-js";
import { selectThemeSettings } from "../../../redux/slices/cmsSettingsSlice";
import { selectSpecificFormSetting } from "../../../redux/slices/formSettingsSlice";
import { selectFormStatus } from "../../../redux/slices/appSettingsSlice";
import { selectAllP2pSettings } from "../../../redux/slices/p2pSettingsSlice";
import { ThemeType } from "../../../redux/constants";
import FormStatus from "./FormStatus";

const ConnectedFormStatus = () => {
    const {
        ENUMS: { FormStatusType },
    } = constants;
    const themeSettings = useSelector((state) =>
        selectThemeSettings(ThemeType.HEADER, state),
    );
    const {
        regHeaderSettings: { pageHeading = "Register for %EventName%" },
        registrationStatus: {
            registrationEndedMessage = "Registration for %EventName% has ended.",
            registrationSoldOutMessage = "Registration for %EventName% has reached capacity and is closed.",
            registrationUnavailableMessage = "Registration for %EventName% is unavailable. Check back later.",
        },
    } = themeSettings;
    const title = useSelector((state) =>
        selectSpecificFormSetting(state, "title"),
    );
    const p2pSettings = useSelector(selectAllP2pSettings);
    const { closedMessage } = p2pSettings;
    const formStatus = useSelector(selectFormStatus);

    const pageHeadingWithEventName = `${
        pageHeading.split("%EventName%")[0]
    }${title}${pageHeading.split("%EventName%")[1]}`;

    const getIndexOfLastCharacterBeforeClosingTag = (thisMessage) =>
        thisMessage.lastIndexOf("</p>");

    const getMessageWithoutPTags = (defaultMessage, thisMessage) => {
        // Remove <p> tags that wrap the CKEditor content by default
        // so that the styling across all versions of this text is
        // consistent
        const indexOfFirstCharacterAfterOpeningTag = 3;

        return thisMessage
            ? thisMessage.substring(
                  indexOfFirstCharacterAfterOpeningTag,
                  getIndexOfLastCharacterBeforeClosingTag(thisMessage),
              )
            : defaultMessage;
    };

    const evaluateForTagsAndSetMessage = (defaultMessage, customizedMessage) =>
        customizedMessage.includes("<p>")
            ? getMessageWithoutPTags(defaultMessage, customizedMessage)
            : customizedMessage;

    const renderMessage = () => {
        // The closedMessage value delivered to the FE is an empty string until
        // a user saves event settings so we accommodate the case where a user
        // sets the status of the form to closed without saving form settings
        const defaultClosedMessageWithoutPTags =
            "This campaign is currently closed.";
        const closedMessageWithoutPTags = getMessageWithoutPTags(
            defaultClosedMessageWithoutPTags,
            closedMessage,
        );

        // Because Registration Ended Closed Message was being set in both the
        // control panel and cms, some custom values will be ported over with
        // rich text formatting.  We are adding the ability for any of these
        // messages to contain rich text
        const defaultRegistrationEndedMessage =
            "Registration for %EventName% has ended.";
        const registrationEndedMessageWithoutPTags =
            evaluateForTagsAndSetMessage(
                defaultRegistrationEndedMessage,
                registrationEndedMessage,
            );

        const defaultRegistrationSoldOutMessage =
            "Registration for %EventName% has reached capacity and is closed.";
        const registrationSoldOutMessageWithoutPTags =
            evaluateForTagsAndSetMessage(
                defaultRegistrationSoldOutMessage,
                registrationSoldOutMessage,
            );

        const defaultRegistrationUnavailableMessage =
            "Registration for %EventName% is unavailable. Check back later.";
        const registrationUnavailableMessageWithoutPTags =
            evaluateForTagsAndSetMessage(
                defaultRegistrationUnavailableMessage,
                registrationUnavailableMessage,
            );

        let message;

        // Get the message
        switch (formStatus) {
            case FormStatusType.EVENT_CLOSED:
                message = closedMessageWithoutPTags;
                break;
            case FormStatusType.REGISTRATION_ENDED:
                message = registrationEndedMessageWithoutPTags;
                break;
            case FormStatusType.REGISTRATION_SOLD_OUT:
                message = registrationSoldOutMessageWithoutPTags;
                break;
            case FormStatusType.REGISTRATION_UNAVAILABLE:
            default:
                // Display registrationUnavailableMessage as a graceful fallback
                message = registrationUnavailableMessageWithoutPTags;
                break;
        }
        // Get the markup
        // Dangerously set the inner HTML as markup is being passed
        // down to the UI from CKEditor
        const formStatusMessage = (
            <div
                className="-user-html-content"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: message.replace(
                        "%EventName%",
                        `<strong>${title}</strong>`,
                    ),
                }}
            />
        );

        return formStatusMessage;
    };
    const documentTitle = pageHeadingWithEventName;

    return (
        <FormStatus
            documentTitle={documentTitle}
            renderMessage={renderMessage}
        />
    );
};

export default ConnectedFormStatus;
